import { create } from 'zustand'
import apiHelper from '@/network/api'

interface Keyword {
  id: string
  keyword: string
  order: number
  createdAt: string
  threadsUserId: string
}

interface KeywordGroup {
  id: string
  teamId: string
  logicOperator: 'AND' | 'OR'
  groupName: string
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  keywords: Keyword[]
}

interface KeywordListState {
  keywordGroups: KeywordGroup[]
  isLoading: boolean
  error: string | null
  fetchKeywordGroups: () => Promise<void>
  setKeywordGroups: (keywordGroups: KeywordGroup[]) => void
  // Legacy setters for compatibility
  setKeywords: (keywords: any[]) => void
  keywords: any[]
  createKeywordGroup: (keywords: string[], logicOperator: 'AND' | 'OR', threadsUserId: string) => Promise<{
    success: boolean,
    error?: number
  }>
  // Legacy method for compatibility
  createKeyword: (keyword: string, purpose: string, threadsUserId: string) => Promise<{
    success: boolean,
    error?: number
  }>
  deleteKeyword: (id: string) => Promise<{
    success: boolean,
    error?: string
  }>
  reorderKeywordGroups: (groupIds: string[]) => Promise<{
    success: boolean,
    error?: string
  }>
}

const useKeywordListStore = create<KeywordListState>((set, get) => ({
  keywordGroups: [],
  keywords: [], // Legacy compatibility
  isLoading: false,
  error: null,

  fetchKeywordGroups: async () => {
    try {
      set({ isLoading: true, error: null })
      const response = await apiHelper(null).get('keyword-groups')
      
      if (response.status === 200) {
        const keywordGroupData = response.data
        set({ 
          keywordGroups: keywordGroupData.data,
          keywords: keywordGroupData.data, // Legacy compatibility
          isLoading: false 
        })
      }
    } catch (error) {
      set({ 
        error: 'Failed to fetch keyword groups',
        isLoading: false
      })
      console.error('Error fetching keyword groups:', error)
    }
  },

  // Legacy method for compatibility
  fetchKeywords: async () => {
    return get().fetchKeywordGroups()
  },

  createKeywordGroup: async (keywords: string[], logicOperator: 'AND' | 'OR', threadsUserId: string) => {
    try {
      const response = await apiHelper(null).post('keyword-groups', { 
        keywords, 
        logicOperator, 
        threadsUserId 
      })
      const data = response.data
      if (data) {
        // Refresh keyword groups list after successful creation
        await get().fetchKeywordGroups()
        return { success: true }
      }
      return { success: false }
    } catch (error: any) {
      if (error.response?.data?.error) {
        return {
          success: false,
          error: error.response.data.error
        }
      }
      return { success: false }
    }
  },

  // Legacy method for compatibility - converts single keyword to keyword group
  createKeyword: async (keyword: string, purpose: string, threadsUserId: string) => {
    return get().createKeywordGroup([keyword], 'OR', threadsUserId)
  },

  deleteKeyword: async (id: string) => {
    try {
      const response = await apiHelper(null).delete(`keyword-groups/${id}`)
      if (response.status === 204) {
        // Refresh keyword groups list after successful deletion
        await get().fetchKeywordGroups()
        return { success: true }
      }
      return { success: false }
    } catch (error) {
      if (error instanceof Error) {
        return {
          success: false,
          error: error.message
        }
      }
      return { success: false }
    }
  },

  setKeywordGroups: (keywordGroups: KeywordGroup[]) => set({
    keywordGroups,
    keywords: keywordGroups // Legacy compatibility
  }),

  // Legacy setter for compatibility
  setKeywords: (keywords: any[]) => set({ keywords }),

  reorderKeywordGroups: async (groupIds: string[]) => {
    try {
      const response = await apiHelper(null).put('keyword-groups/reorder', { groupIds })
      if (response.status === 200) {
        // Refresh keyword groups list after successful reorder
        await get().fetchKeywordGroups()
        return { success: true }
      }
      return { success: false }
    } catch (error) {
      if (error instanceof Error) {
        return {
          success: false,
          error: error.message
        }
      }
      return { success: false }
    }
  }
}))

export default useKeywordListStore
